/**
 * The kinds of thing a name can refer to — one word and one icon each, used by
 * ObjectRef everywhere a cycle, track, session, list or item type is named.
 */
import {
  Award,
  Calendar,
  CircleDot,
  ClipboardList,
  Coins,
  Compass,
  Filter,
  Flag,
  Gauge,
  Gavel,
  Handshake,
  Inbox,
  Layers,
  Link2,
  Map as MapIcon,
  ListChecks,
  ListOrdered,
  Mountain,
  RefreshCcw,
  Rocket,
  ScrollText,
  Shapes,
  Tag,
  Target,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

export type ObjectKind =
  // where an item is evaluated
  | 'cycle'
  | 'track'
  | 'session'
  | 'loop'
  | 'ball'
  | 'proposal'
  | 'decision'
  // lists (canon 03 §2.11A) and the plan
  | 'collection'
  | 'item_pool'
  | 'session_result'
  | 'priority_set'
  | 'plan'
  // a promise the organization has made (glossary — Commitment)
  | 'commitment'
  | 'smart_set'
  // strategy: what the work serves (glossary — Strategy, Strategic Objectives)
  | 'strategy'
  | 'objective'
  | 'goal'
  // the types an item carries
  | 'category'
  | 'altitude'
  | 'work_type'
  | 'launch_type'
  | 'investment_type'
  | 'strategic_bucket'
  | 'subject_type'
  | 'source';

export interface ObjectKindDef {
  /** The word shown before the name, in small capitals. */
  word: string;
  icon: LucideIcon;
  /** The page for one of these, when it has one. */
  href?: (id: string) => string;
}

export const OBJECT_KINDS: Record<ObjectKind, ObjectKindDef> = {
  cycle: { word: 'Cycle', icon: Calendar, href: (id) => `/prioritization/cycles/${id}` },
  track: { word: 'Track', icon: Layers, href: (id) => `/prioritization/tracks/${id}` },
  session: { word: 'Session', icon: Target, href: (id) => `/prioritization/sessions/${id}` },
  loop: { word: 'Loop', icon: RefreshCcw },
  ball: { word: 'Ball', icon: CircleDot },
  proposal: { word: 'Proposal', icon: ScrollText, href: (id) => `/prioritization/governance/proposals/${id}` },
  decision: { word: 'Decision', icon: Gavel, href: (id) => `/prioritization/decisions/${id}` },
  collection: { word: 'Collection', icon: Link2, href: (id) => `/prioritization/collections/${id}` },
  item_pool: { word: 'Item pool', icon: ListChecks, href: (id) => `/prioritization/lists/${id}` },
  session_result: { word: 'Session result', icon: ListOrdered, href: (id) => `/prioritization/sessions/${id}/results` },
  track_result: { word: 'Track result', icon: ListOrdered, href: (id) => `/prioritization/tracks/${id}/result` },
  priority_set: { word: 'Priority list', icon: Award, href: (id) => `/prioritization/reports/priority-set/${id}` },
  plan: { word: 'Plan of record', icon: ClipboardList, href: (id) => `/planning/plans/${id}` },
  commitment: { word: 'Commitment', icon: Handshake, href: (id) => `/prioritization/commitments/${id}` },
  smart_set: { word: 'Smart set', icon: Filter },
  strategy: { word: 'Strategy', icon: MapIcon, href: (id) => `/prioritization/strategy/${id}` },
  objective: { word: 'Objective', icon: Flag },
  goal: { word: 'Goal', icon: Gauge },
  category: { word: 'Category', icon: Tag },
  altitude: { word: 'Altitude', icon: Mountain },
  work_type: { word: 'Work type', icon: Wrench },
  launch_type: { word: 'Launch type', icon: Rocket },
  investment_type: { word: 'Investment type', icon: Coins },
  strategic_bucket: { word: 'Strategic bucket', icon: Compass },
  subject_type: { word: 'Subject type', icon: Shapes },
  source: { word: 'Source', icon: Inbox },
};
